/**
 * @copyright Copyright 2023-2025 Epic Systems Corporation
 * @file app thumbnail or thumbnail placeholder
 * @module Epic.AppOrchard.AppListing.Thumbnail
 */

import { ExternalLinkIcon } from "@chakra-ui/icons";
import { Box, BoxProps, Image, ImageProps } from "@chakra-ui/react";
import { config } from "ao/appConfig";
import { getBaseUrl, isNullOrEmpty } from "ao/utils/helpers";
import React, { FunctionComponent, memo, useMemo } from "react";

interface IProps {
	src?: string;
	listingId: number;
	showBackground?: boolean;
}

type Props = IProps & ImageProps;

export const Thumbnail: FunctionComponent<Props> = memo((props) => {
	const { src, listingId, showBackground, ...rest } = props;

	const baseUrl = useMemo(() => getBaseUrl(), []);

	const thumbnailSrc = !isNullOrEmpty(src)
		? `data:image/png;base64,${src}`
		: `${baseUrl}Content/images/app_placeholder.png`;
	const thumbnailColor = !isNullOrEmpty(src)
		? "none"
		: `appThumbnailBackground.${listingId % config.NumAppThumbnailColors}`;

	return (
		<Image
			src={thumbnailSrc}
			backgroundColor={showBackground ? rest.backgroundColor || thumbnailColor : "none"}
			{...rest}
		/>
	);
});

interface IThumbnailStageWatermarkProps extends BoxProps {
	iconPath: string;
}

export const ThumbnailStageWatermark: React.FC<IThumbnailStageWatermarkProps> = (
	props: IThumbnailStageWatermarkProps,
) => {
	const baseUrl = useMemo(() => getBaseUrl(), []);

	return (
		<ThumbnailWatermark {...props}>
			<Image
				src={`${baseUrl}Content/images/${props.iconPath}`}
				fallback={<></>}
				maxH="26px"
				maxW="26px"
				h="26px"
				w="26px"
				p="3px"
				pos="absolute"
				right="3px"
				style={{
					filter:
						"drop-shadow(1px 0px 0px white) drop-shadow(-1px 0px 0px white) drop-shadow(0px 1px 0px white) drop-shadow(0px -1px 0px white)",
				}}
			/>
		</ThumbnailWatermark>
	);
};

interface IThumbnailExternalWatermarkProps {
	title?: string;
}

export const ThumbnailExternalWatermark: React.FC<IThumbnailExternalWatermarkProps> = (
	props: IThumbnailExternalWatermarkProps,
) => {
	return (
		<ThumbnailWatermark title={props.title}>
			<ExternalLinkIcon maxH="26px" maxW="26px" h="26px" w="26px" p="3px" />
		</ThumbnailWatermark>
	);
};

export const ThumbnailWatermark: React.FC<BoxProps> = (props: BoxProps) => {
	return (
		<Box
			pos={props?.pos ?? props.position ?? "absolute"}
			top={props?.top ?? "5px"}
			right={props?.right ?? "5px"}
			border={props?.border ?? "0"}
			borderBottom={props?.borderBottom ?? "0"}
			borderLeft={props?.borderLeft ?? "0"}
			backgroundColor={props?.backgroundColor ?? "white"}
			backgroundImage={props?.backgroundImage ?? "none"}
			backgroundSize={props?.backgroundSize ?? "contain"}
			backgroundRepeat={props?.backgroundRepeat ?? "no-repeat"}
			backgroundPosition={props?.backgroundPosition ?? "top right"}
			borderRadius={props?.borderRadius ?? "50%"}
			padding={props?.padding ?? "2px 2px 5px 5px"}
			height={props?.h ?? props.height ?? "auto"}
			width={props?.w ?? props.width ?? "auto"}
			maxH={props?.maxH ?? props?.maxHeight ?? "100%"}
			maxW={props?.maxW ?? props.maxWidth ?? "84px"}
			title={props.title}
		>
			{props.children}
		</Box>
	);
};

Thumbnail.defaultProps = { display: "inline", borderRadius: "10px" };
